/**
 * Which emails this person gets.
 *
 * Mirrors MAIL_KINDS and DEFAULT_MAIL_PREFS in functions/src/email/orgMail.ts.
 * Read from the member document (a person may read their own); written
 * through a callable, because the rules let a member change only their theme
 * on that document and the server keeps the audit entry.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';

const db = getFirestore(app);

export const MAIL_KINDS = [
  'approvalRequests',
  'approvalOutcomes',
  'reviewReminders',
  'acknowledgementReminders',
  'ownership',
  'retired',
  'stalledApprovals',
] as const;

export type MailKind = (typeof MAIL_KINDS)[number];

export const DEFAULT_PREFS: Record<MailKind, boolean> = {
  approvalRequests: true,
  approvalOutcomes: true,
  reviewReminders: true,
  acknowledgementReminders: true,
  ownership: true,
  retired: false,
  stalledApprovals: true,
};

/** Wording, from the reader's side. `roles` says who the message can reach. */
export const MAIL_LABELS: Record<MailKind, { label: string; hint: string; forRole: 'everyone' | 'author' | 'reviewer' | 'admin' }> = {
  approvalRequests: { label: 'A document needs my approval', hint: 'Sent the moment a step your role holds opens.', forRole: 'reviewer' },
  approvalOutcomes: { label: 'Something I submitted was approved or returned', hint: 'With the approver\'s comments when it comes back.', forRole: 'author' },
  reviewReminders: { label: 'A policy I own is coming due for review', hint: '90, 60 and 30 days before, then when it is overdue.', forRole: 'author' },
  acknowledgementReminders: { label: 'Policies I still have to acknowledge', hint: 'A daily note while something is due within a week or overdue.', forRole: 'everyone' },
  ownership: { label: 'I have been made the owner of a policy', hint: '', forRole: 'author' },
  retired: { label: 'A policy I own was retired', hint: 'Off unless you want it.', forRole: 'author' },
  stalledApprovals: { label: 'Approvals have stalled', hint: 'A weekly list of anything waiting on one step for two weeks or more.', forRole: 'admin' },
};

export function mergePrefs(stored: unknown): Record<MailKind, boolean> {
  const out = { ...DEFAULT_PREFS };
  if (stored && typeof stored === 'object') {
    for (const kind of MAIL_KINDS) {
      const v = (stored as Record<string, unknown>)[kind];
      if (typeof v === 'boolean') out[kind] = v;
    }
  }
  return out;
}

export function useNotificationPrefs(orgId: string | null, uid: string | null) {
  return useQuery({
    queryKey: ['notification-prefs', orgId, uid],
    enabled: Boolean(orgId && uid),
    queryFn: async () => {
      const snap = await getDoc(doc(db, 'orgs', orgId!, 'members', uid!));
      return mergePrefs(snap.exists() ? snap.data().notifications : null);
    },
  });
}

export function useSaveNotificationPrefs(orgId: string | null, uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notifications: Partial<Record<MailKind, boolean>>) =>
      call<{ notifications: Partial<Record<MailKind, boolean>> }, { notifications: Record<MailKind, boolean> }>(
        'updateNotificationPreferences',
        { notifications },
      ),
    onSuccess: (data) => {
      qc.setQueryData(['notification-prefs', orgId, uid], mergePrefs(data.notifications));
    },
  });
}
